import { useEffect, useRef, useState } from "react";

import { api, ApiError } from "../lib/api.js";
import { IconPaperclip, IconSent } from "./icons.js";
import type { MailAddress, MessageDetail, OutgoingMessage, UploadedAttachment } from "../lib/types.js";

/**
 * Opstelvenster voor een nieuw bericht, een antwoord of een doorstuurbericht.
 *
 * De threading-velden (inReplyTo/references) worden hier alleen doorgegeven —
 * de server bouwt de keten op. Zonder die twee begint elke reply bij de
 * ontvanger een losse conversatie, en dat merk je zelf nooit.
 */

export type ComposeMode = "new" | "reply" | "replyAll" | "forward";

export interface ComposeSeed {
  mode: ComposeMode;
  /** Het bericht waarop dit een reactie is; leeg bij een nieuw bericht. */
  source?: { folder: string; message: MessageDetail };
}

interface Props {
  seed: ComposeSeed;
  self: string;
  onClose: () => void;
  onSent: () => void;
}

/** "Naam <adres>, adres2" → losse adressen. Tolerant, want mensen plakken van alles. */
function parseAddresses(input: string): MailAddress[] {
  return input
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter((part) => part !== "")
    .map((part) => {
      const match = /^(.*?)\s*<([^>]+)>$/.exec(part);
      if (match) {
        return { name: (match[1] ?? "").replace(/^["']|["']$/g, "").trim(), address: (match[2] ?? "").trim() };
      }
      return { name: "", address: part };
    })
    .filter((a) => a.address.includes("@"));
}

function formatAddresses(list: MailAddress[]): string {
  return list.map((a) => (a.name ? `${a.name} <${a.address}>` : a.address)).join(", ");
}

function stripPrefixes(subject: string): string {
  let s = subject;
  for (let i = 0; i < 10; i++) {
    const next = s.replace(/^\s*(re|antw|aw|sv|fwd?|doorst|wg)\s*(\[\d+\])?\s*:\s*/i, "");
    if (next === s) break;
    s = next;
  }
  return s.trim();
}

function quoted(message: MessageDetail): string {
  const who = message.from[0]?.name || message.from[0]?.address || "onbekend";
  const when = new Date(message.date).toLocaleString("nl-NL", {
    day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
  return `\n\nOp ${when} schreef ${who}:\n${message.text.split("\n").map((l) => `> ${l}`).join("\n")}\n`;
}

function seedFields(seed: ComposeSeed, self: string): {
  to: string; cc: string; subject: string; text: string;
} {
  const src = seed.source?.message;
  if (!src) return { to: "", cc: "", subject: "", text: "" };

  if (seed.mode === "forward") {
    return {
      to: "", cc: "",
      subject: `Fwd: ${stripPrefixes(src.subject)}`,
      text: `\n\n---------- Doorgestuurd bericht ----------\nVan: ${formatAddresses(src.from)}\nDatum: ${new Date(src.date).toLocaleString("nl-NL")}\nOnderwerp: ${src.subject}\nAan: ${formatAddresses(src.to)}\n\n${src.text}`,
    };
  }

  // Antwoorden gaat naar Reply-To als die er is, anders naar de afzender.
  const target = src.replyTo.length > 0 ? src.replyTo : src.from;
  const me = self.toLowerCase();
  const extra = seed.mode === "replyAll"
    ? [...src.to, ...src.cc].filter(
        (a) =>
          a.address.toLowerCase() !== me &&
          !target.some((t) => t.address.toLowerCase() === a.address.toLowerCase()),
      )
    : [];

  return {
    to: formatAddresses(target),
    cc: formatAddresses(extra),
    subject: `Re: ${stripPrefixes(src.subject)}`,
    text: quoted(src),
  };
}


export function Composer({ seed, self, onClose, onSent }: Props): React.JSX.Element {
  const initial = seedFields(seed, self);
  const [to, setTo] = useState(initial.to);
  const [cc, setCc] = useState(initial.cc);
  const [showCc, setShowCc] = useState(initial.cc !== "");
  const [subject, setSubject] = useState(initial.subject);
  const [text, setText] = useState(initial.text);
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Bij een antwoord staat de cursor bovenaan, boven het citaat — daar begint
  // je tekst, niet onderaan.
  useEffect(() => {
    const el = bodyRef.current;
    if (el && seed.mode !== "new") {
      el.focus();
      el.setSelectionRange(0, 0);
    }
  }, [seed.mode]);

  // Escape sluit het venster, maar alleen als er nog niets is ingevuld —
  // anders gooi je met één toetsaanslag een half getypt bericht weg.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key !== "Escape") return;
      if (to.trim() === "" && subject.trim() === "" && text.trim() === initial.text.trim()) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [to, subject, text, initial.text, onClose]);

  async function pickFiles(list: FileList | null): Promise<void> {
    if (!list || list.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(list)) {
        const uploaded = await api.uploadAttachment(file);
        setAttachments((prev) => [...prev, uploaded]);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Uploaden mislukt.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function removeAttachment(id: string): Promise<void> {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    await api.discardAttachment(id).catch(() => undefined);
  }

  function buildDraft(): OutgoingMessage {
    const src = seed.source?.message;
    const isReply = seed.mode === "reply" || seed.mode === "replyAll";
    return {
      to: parseAddresses(to),
      cc: parseAddresses(cc),
      bcc: [],
      subject,
      text,
      attachments: attachments.map((a) => ({
        id: a.id, filename: a.filename, contentType: a.contentType,
      })),
      ...(isReply && src?.messageId ? { inReplyTo: src.messageId } : {}),
      references: isReply && src ? [...src.references, ...(src.messageId ? [src.messageId] : [])] : [],
      ...(isReply && src && seed.source
        ? { answering: { folder: seed.source.folder, uid: src.uid } }
        : {}),
    };
  }

  async function send(): Promise<void> {
    const draft = buildDraft();
    if (draft.to.length === 0) {
      setError("Vul minstens één geldig e-mailadres in bij Aan.");
      return;
    }
    if (draft.text.trim() === "") {
      setError("Het bericht is leeg.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.send(draft);
      onSent();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Versturen mislukt.");
    } finally {
      setBusy(false);
    }
  }

  async function storeDraft(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.saveDraft(buildDraft());
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Opslaan mislukt.");
    } finally {
      setBusy(false);
    }
  }

  const title =
    seed.mode === "forward" ? "Doorsturen"
    : seed.mode === "new" ? "Nieuw bericht"
    : "Beantwoorden";

  return (
    <div className="composer-backdrop" role="dialog" aria-modal="true" aria-label={title}>
      <div className="composer">
        <header className="composer-head">
          <h2>{title}</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten">×</button>
        </header>

        <div className="composer-fields">
          <label className="composer-row">
            <span>Aan</span>
            <input
              type="text" value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="naam@voorbeeld.nl" autoComplete="off" spellCheck={false}
            />
          </label>

          {showCc ? (
            <label className="composer-row">
              <span>Cc</span>
              <input
                type="text" value={cc} onChange={(e) => setCc(e.target.value)}
                autoComplete="off" spellCheck={false}
              />
            </label>
          ) : (
            <button type="button" className="link-button composer-cc-toggle" onClick={() => setShowCc(true)}>
              Cc toevoegen
            </button>
          )}

          <label className="composer-row">
            <span>Onderwerp</span>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </label>
        </div>

        <textarea
          ref={bodyRef}
          className="composer-body"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Typ uw bericht…"
        />

        {attachments.length > 0 && (
          <ul className="composer-attachments">
            {attachments.map((a) => (
              <li key={a.id}>
                <span className="attachment-name">{a.filename}</span>
                <span className="attachment-size">{Math.max(1, Math.round(a.size / 1024))} kB</span>
                <button type="button" className="icon-button" onClick={() => void removeAttachment(a.id)}
                  aria-label={`${a.filename} verwijderen`}>×</button>
              </li>
            ))}
          </ul>
        )}

        {error !== null && <p className="composer-error" role="alert">{error}</p>}

        <footer className="composer-actions">
          <button
            type="button"
            className="btn btn--send"
            onClick={() => void send()}
            disabled={busy || uploading}
          >
            <IconSent />
            {busy ? "Bezig…" : "Verzenden"}
          </button>

          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            title="Bestand toevoegen"
          >
            <IconPaperclip />
            {uploading ? "Uploaden…" : "Bijlage"}
          </button>

          {/* Concept staat rechts en bewust rustiger: het is de uitweg, niet de
              hoofdhandeling. */}
          <button
            type="button"
            className="btn btn--text composer-draft"
            onClick={() => void storeDraft()}
            disabled={busy}
          >
            Opslaan als concept
          </button>

          <input
            ref={fileInput} type="file" multiple hidden
            onChange={(e) => void pickFiles(e.target.files)}
          />
        </footer>
      </div>
    </div>
  );
}
